/**
 * Pre-visit / self-service patient intake (`BACKLOG.md` C17's "pre-visit-history-intake slice" —
 * read that item's own row before touching this file: the *rest* of C17 as originally scoped, a
 * demographics/insurance portal-vs-vendor-point-solution decision, is explicitly **not** settled and
 * explicitly **not** built here; see this module's own limits below). A fifth patient-scoped-token
 * consumer (`patientScopedTokens.ts`'s own header names this file directly), reusing that token
 * mechanism and `C10b`'s form-template engine rather than inventing either — the identical reuse
 * argument `patientSurveys.ts`'s own header already makes for the sibling `patient_survey` purpose.
 *
 * **What this is**: a staff member picks a patient and a patient-visible form template (a medical-
 * history questionnaire, most naturally), issues a link, and it goes out by email
 * (`server/src/auth/emailSender.ts`'s `sendPreVisitIntakeLink` — a stub today, same "not a silent
 * no-op" posture as every other email that module sends) for the patient to fill out before they
 * arrive — no portal registration, no staff session, nothing but the link itself.
 *
 * **Deliberately not a `form_submissions` row** — identical reasoning to `patientSurveys.ts`'s own
 * header: that table's `submittedByUserId` is a `NOT NULL` foreign key into `users`, and a patient
 * completing this through an unauthenticated token holds neither a `users` row nor a staff session.
 * `patient_intake_submissions` is its own sibling table for the same reason `patient_survey_responses`
 * and `patient_license_scans` are each their own table rather than a shared one.
 *
 * **Deliberately not gated on TCPA/SMS consent** — unlike `patient_survey`, this purpose is delivered
 * by email, not SMS, so `patientSurveys.ts`'s `SMS_CONSENT_GRANT_METHODS`/`isSmsConsentActive` gate
 * does not apply; there is no equivalent CAN-SPAM-style per-send consent gate built here, matching
 * this codebase's existing posture for its other transactional emails (verification, password reset,
 * new-message notification) — none of which gate on a recorded consent event either.
 *
 * **Which templates are eligible** — no new `form_templates` column. A template is eligible for a
 * pre-visit-intake link exactly when it is not archived (`findFormTemplateVersionById`'s ordinary
 * resolution); the *narrower* question of which of an org's templates are "meant for patients" versus
 * "internal staff documentation" is left to the issuing staff member's own judgment for this v1, the
 * same way `C10b`'s admin template picker does not itself distinguish patient-facing from
 * clinician-only templates. Revisit if that distinction ever needs enforcing rather than trusting.
 */

import { z } from 'zod';

import { formFieldsSchema, formSubmissionValuesSchema } from './formTemplates.ts';

// --- Token issuance -------------------------------------------------------------------------------

export const PATIENT_INTAKE_TOKEN_ERROR_CODES = [
  'invalid_request',
  'not_found',
  /** The named template version exists but is archived — matches `PATIENT_SURVEY_TOKEN_ERROR_CODES`'
   * identical `template_archived` reasoning. */
  'template_archived',
  /** The patient has no email address on file to send the link to. */
  'patient_missing_email',
] as const;
export type PatientIntakeTokenErrorCode = (typeof PATIENT_INTAKE_TOKEN_ERROR_CODES)[number];

export const createPatientIntakeTokenRequestSchema = z.strictObject({
  patientId: z.uuid(),
  templateId: z.uuid(),
});
export type CreatePatientIntakeTokenRequest = z.infer<typeof createPatientIntakeTokenRequestSchema>;

/**
 * Deliberately carries no raw token — mirrors `patientSurveyTokenResponseSchema`'s own reasoning
 * exactly: this token is delivered exactly once, by email, and echoing it back into the staff-facing
 * API response would be a second egress path for the same credential with no corresponding need
 * (`claude-kit/rules/invariants.md`'s "Secrets travel once").
 */
export const patientIntakeTokenResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  /** `null` only in the unreachable case where a stored token's own `scopeData` failed to resolve
   * (`resolvePatientIntakeScope` returning `null`) — every token this build issues always sets it,
   * matching `resolvePatientSurveyScope`'s identical fail-safe posture for untrusted `jsonb`
   * storage. */
  templateId: z.uuid().nullable(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PatientIntakeTokenResponse = z.infer<typeof patientIntakeTokenResponseSchema>;

export const createPatientIntakeTokenResponseSchema = z.object({
  token: patientIntakeTokenResponseSchema,
});
export type CreatePatientIntakeTokenResponse = z.infer<
  typeof createPatientIntakeTokenResponseSchema
>;

export const listPatientIntakeTokensResponseSchema = z.object({
  tokens: z.array(patientIntakeTokenResponseSchema),
});
export type ListPatientIntakeTokensResponse = z.infer<typeof listPatientIntakeTokensResponseSchema>;

// --- The token-scoped intake surface itself --------------------------------------------------------

/**
 * What the intake-fill page renders — the pinned template's own fields at issue time, matching
 * `patientSurveyViewResponseSchema`'s identical versioning guarantee: a template edited after this
 * link was sent must never change what this specific patient is shown.
 */
export const patientIntakeViewResponseSchema = z.object({
  templateId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  fields: formFieldsSchema,
  expiresAt: z.string(),
});
export type PatientIntakeViewResponse = z.infer<typeof patientIntakeViewResponseSchema>;

export const submitPatientIntakeRequestSchema = z.strictObject({
  values: formSubmissionValuesSchema,
});
export type SubmitPatientIntakeRequest = z.infer<typeof submitPatientIntakeRequestSchema>;

export const submitPatientIntakeResponseSchema = z.object({ submitted: z.literal(true) });

export const PATIENT_INTAKE_SUBMIT_ERROR_CODES = ['invalid_request', 'invalid_values'] as const;
export type PatientIntakeSubmitErrorCode = (typeof PATIENT_INTAKE_SUBMIT_ERROR_CODES)[number];

// --- Staff-visible submissions ------------------------------------------------------------------

export const patientIntakeSubmissionRecordSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  templateId: z.uuid(),
  values: formSubmissionValuesSchema,
  submittedAt: z.string(),
  createdAt: z.string(),
});
export type PatientIntakeSubmissionRecord = z.infer<typeof patientIntakeSubmissionRecordSchema>;

export const listPatientIntakeSubmissionsResponseSchema = z.object({
  submissions: z.array(patientIntakeSubmissionRecordSchema),
});
